import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "container no-user-select cluster-view" }

import { ref, useTemplateRef, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { until } from '@vueuse/core';

import { useRouteIs } from '@services/route-is';
import { useRouteState } from '@services/route-state';
import { isAbortError, raceWithAbort, useAbort } from '@services/utils/abort-vue';
import TopMatter from '@components/top-matter/TopMatter.vue';
import ClusterGrid from '@components/ClusterGrid.vue';
import EmptyContent from '@components/top-matter/EmptyContent.vue';
import DynamicTopMatter from '@components/top-matter/DynamicTopMatter.vue';

import { config } from '@services/user-config';
import * as dav from '@services/dav';

import type { ICluster } from '@typings';


export default /*@__PURE__*/_defineComponent({
  __name: 'ClusterView',
  setup(__props) {

const route = useRoute();
const routeIs = useRouteIs();
const dtmRef = useTemplateRef<InstanceType<typeof DynamicTopMatter>>('dtm');
const gridRef = useTemplateRef<VueHTMLComponent>('grid');
const items = ref<ICluster[]>([]);
const loading = ref(0);
const abort = useAbort();

useRouteState({
  state: { items },
  scroll: { gridRef },
});

const minCols = computed(() => (routeIs.Albums ? 2 : 3));
const maxSize = computed(() => (routeIs.Albums ? 250 : 180));

async function fetchClusters(signal: AbortSignal): Promise<ICluster[]> {
  if (routeIs.Albums) {
    return await dav.getAlbums({ signal });
  } else if (routeIs.Tags) {
    return await dav.getTags({ signal });
  } else if (routeIs.Recognize) {
    return await dav.getFaceList('recognize', { signal });
  } else if (routeIs.FaceRecognition) {
    return await dav.getFaceList('facerecognition', { signal });
  } else if (routeIs.Places) {
    return await dav.getPlaces({ signal });
  } else {
    return [];
  }
}

async function refresh() {
  const signal = abort.renew();

  try {
    loading.value++;

    // Refresh the DTM in parallel with loading our own data,
    // but wait for it to complete to avoid glitches.
    const [, newItems] = await Promise.all([
      raceWithAbort(signal, until(() => dtmRef.value?.isReady).toBeTruthy({ timeout: 2000 })),
      fetchClusters(signal),
    ]);
    signal.throwIfAborted();
    items.value = newItems;
  } catch (e) {
    if (isAbortError(e)) return;
    console.error(e);
  } finally {
    loading.value--;
  }
}

onMounted(refresh);
watch(() => route.path, refresh, { flush: 'post' });
watch(config, refresh);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(TopMatter),
    (!items.value.length && !loading.value)
      ? (_openBlock(), _createBlock(EmptyContent, { key: 0 }))
      : _createCommentVNode("", true),
    _createVNode(ClusterGrid, {
      ref: "grid",
      items: items.value,
      minCols: minCols.value,
      maxSize: maxSize.value,
      focus: true,
      loading: !!loading.value
    }, {
      before: _withCtx(() => [
        _createVNode(DynamicTopMatter, {
          class: "cv-dtm",
          ref: "dtm"
        }, null, 512)
      ]),
      _: 1
    }, 8, ["items", "minCols", "maxSize", "loading"])
  ]))
}
}

})